import { t } from 'i18next';
import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import { Stack, Typography } from '@mui/material';
import Pagination from '@mui/material/Pagination';

import { PageOptions } from 'src/services/api.types';
import { useDeleteCommentMutation, useGetThreadQuery, useLazyGetCommentsThreadQuery } from 'src/services/threads/threads.service';

import ListSkeleton from 'src/components/list/list-skeleton';

import { useBoolean } from 'src/hooks/use-boolean';
import { Comment, IThread } from 'src/services/threads/threads.types';
import { useAppDispatch } from 'src/store/store';
import { rootApi } from 'src/services/rootApi';
import { usePopover } from 'src/components/custom-popover';
import { ConfirmDialog } from 'src/components/custom-dialog';
import { LoadingButton } from '@mui/lab';
import { enqueueSnackbar } from 'notistack';
import CommentEditor from './comment-editor';
import PostCommentItem from './post-comment-item';

// ----------------------------------------------------------------------

type Props = {
  thread_id?: string;
  comments_per_page?: number;
};

const DEFAULT_PAGE = {
  page: 1,
  per_page: 5,
};

export default function PostCommentList({ thread_id, comments_per_page = DEFAULT_PAGE.per_page }: Props) {

  const commentEditorControl = useBoolean();
  const commentDeleteControl = useBoolean();

  const dispatch = useAppDispatch();
  const [currentComment, setCurrentComment] = useState<Comment | null>(null);

  const [deleteComment, { isLoading: isDeleting }] = useDeleteCommentMutation();

  const [page, setPage] = useState<PageOptions>({
    page: DEFAULT_PAGE.page,
    per_page: comments_per_page,
  });

  const { currentData: thread, isLoading: isLoadingThread } = useGetThreadQuery({
    thread_id: thread_id as string
  }, {
    skip: !thread_id
  });

  const [getComments, { currentData }] = useLazyGetCommentsThreadQuery();

  useEffect(() => {
    if (!thread_id) return;

    getComments({
      thread_id,
      ...page,
    });
  }, [getComments, page, thread_id]);

  const onChangePage = (_event: any, newPage: number) => {
    setPage((prev) => ({
      ...prev,
      page: newPage,
    }));
  };

  const openEditor = (comment: Comment) => {
    setCurrentComment(comment);
    commentEditorControl.onTrue();
  }

  const closeEditor = () => {
    setCurrentComment(null);
    commentEditorControl.onFalse();
  }

  const onCommentEdit = () => {
    // Refresh comments
    dispatch(rootApi.util.invalidateTags([{ type: 'Comments', id: thread_id as string }]));
    closeEditor();
  }

  const openDeleteConfirm = (comment: Comment) => {
    setCurrentComment(comment);
    commentDeleteControl.onTrue();
  }

  const closeDeleteConfirm = useCallback(() => {
    setCurrentComment(null);
    commentDeleteControl.onFalse();
  }, [commentDeleteControl]);

  const handleCommentDelete = useCallback(async () => {
    if (!currentComment) return;

    try {
      await deleteComment({
        thread_id: thread_id as string,
        comment_id: currentComment.id
      }).unwrap();

      closeDeleteConfirm();
      enqueueSnackbar(t('threads.api.delete_comment.success'), { variant: 'success' });
    }
    catch (error) {
      enqueueSnackbar(t('threads.api.delete_comment.default_error'), { variant: 'error' });
    }
  }, [currentComment, deleteComment, thread_id, closeDeleteConfirm]);

  return (
    <Box id="comment-list">

      {
        isLoadingThread && (
          <Stack flexDirection="column">
            <ListSkeleton height={40} />
          </Stack>
        )
      }

      {
        !isLoadingThread && (
          <>
            {currentData?.results.map((comment) => {
              const {
                id,
                content,
                type,
                data,
                created_at,
                account: { first_name, last_name },
              } = comment;

              return (
                <Box key={id}>
                  <PostCommentItem
                    comment={comment}
                    onEdit={() => openEditor(comment)}
                    onDelete={() => openDeleteConfirm(comment)}
                  />
                </Box>
              );
            })}
          </>
        )
      }

      {!!currentData?.count &&
        <Stack flexDirection="row" justifyContent="center">
          <Pagination
            onChange={onChangePage}
            page={page.page}
            count={
              currentData?.count && currentData.count > page.per_page
                ? Math.ceil(currentData.count / page.per_page)
                : 1
            }
            sx={{ mt: 1, mx: 'auto' }}
          />
        </Stack>
      }

      {!currentData?.count &&
        <Stack flexDirection="row" justifyContent="center" sx={{ mt: 3 }}>
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            {t(`threads.comments.empty_${thread?.type ?? 'chat'}`)}
          </Typography>
        </Stack>
      }

      {
        (commentEditorControl.value && thread) && (
          <CommentEditor
            thread={thread as IThread}
            comment={currentComment as Comment}
            open={commentEditorControl.value}
            onClose={closeEditor}
            onSuccess={onCommentEdit}
          />
        )
      }

      <ConfirmDialog
        open={commentDeleteControl.value}
        onClose={commentDeleteControl.onFalse}
        title={t('threads.comments.delete_comment')}
        content={t('threads.comments.delete_confirm')}
        action={
          <LoadingButton variant="contained" color="error" onClick={handleCommentDelete} loading={isDeleting}>
            {t('common.delete')}
          </LoadingButton>
        }
      />
    </Box>
  );
}
